import React, { useState } from "react";
import {
  deleteIngredientPurchaseRecord
} from "../../../../../../crud/management_api/ingredient_purchases";
import { HiOutlinePencilSquare, HiOutlineTrash } from "react-icons/hi2";
import { FaInbox } from "react-icons/fa";
import { formatCurrency } from "../../../../../../utils/functions";
import { IngredientPurchaseRecord, ExpenseCategoryType } from "../../types";
import EditPurchase from "./components/EditPurchase/EditPurchase";
import "./Table.scss";

interface PurchasesTableProps {
  data: IngredientPurchaseRecord[];
  setData?: (data: IngredientPurchaseRecord[]) => void;
  fetchPurchases: () => Promise<void>;
  categories: ExpenseCategoryType[];
}

const PurchasesTable: React.FC<PurchasesTableProps> = ({ data, fetchPurchases, categories }) => {
  const [showEditModal, setShowEditModal] = useState(false);
  const [editPurchase, setEditPurchase] = useState<IngredientPurchaseRecord | null>(null);

  const handleEdit = (record: IngredientPurchaseRecord) => {
    setEditPurchase(record);
    setShowEditModal(true);
  };

  const handleDelete = async (record: IngredientPurchaseRecord) => {
    if (window.confirm("¿Estás seguro de que quieres eliminar esta compra?")) {
      try {
        await deleteIngredientPurchaseRecord(record.ingredient_purchase_id);
        fetchPurchases();
      } catch (error) {
        console.error("Error al eliminar la compra:", error);
        alert("Error al eliminar la compra. Por favor, inténtalo de nuevo.");
      }
    }
  };

  const renderTaxBadge = (taxType: string | null) => {
    if (!taxType) return <span style={{ opacity: 0.4 }}>—</span>;
    if (taxType === 'deducible_iva') return <span className="tax-badge deducible">Factura</span>;
    if (taxType === 'no_deducible_iva') return <span className="tax-badge no-deducible">Boleta</span>;
    if (taxType === 'no_recuperable') return <span className="tax-badge no-recuperable">No recup.</span>;
    return <span style={{ opacity: 0.4 }}>—</span>;
  };

  const renderDesktopView = () => (
    <div className="mp-table-card compras-table-card">
      <table className="mp-table">
        <thead className="mp-table-head">
          <tr>
            <th>Producto</th>
            <th>Proveedor</th>
            <th className="no-responsive">Fecha</th>
            <th>Cantidad</th>
            <th className="no-responsive">Unidad</th>
            <th>Total Neto</th>
            <th className="no-responsive">Total c/IVA</th>
            <th className="no-responsive">IVA</th>
            <th className="no-responsive">Categoría</th>
            <th className="no-responsive">Precio Unit.</th>
            <th style={{ width: 90, textAlign: 'center' }}>Acciones</th>
          </tr>
        </thead>
        <tbody className="mp-table-body">
          {data.map((record) => (
            <tr key={record.ingredient_purchase_id}>
              <td className="font-medium">{record.ingredient_name || <span style={{ opacity: 0.4 }}>—</span>}</td>
              <td>
                {record.provider_name ? (
                  <span className="provider-badge">{record.provider_name}</span>
                ) : (
                  <span style={{ opacity: 0.4 }}>N/A</span>
                )}
              </td>
              <td className="no-responsive" style={{ opacity: 0.7 }}>
                {record.purchase_date
                  ? new Date(record.purchase_date).toLocaleDateString()
                  : record.timestamp
                    ? new Date(record.timestamp).toLocaleDateString()
                    : 'Sin fecha'}
              </td>
              <td style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{record.quantity.toFixed(2)}</td>
              <td className="no-responsive">
                <span className="unit-badge">{record.unidad_medida_name || '—'}</span>
              </td>
              <td style={{ fontWeight: 600, color: '#FC7556', fontVariantNumeric: 'tabular-nums' }}>
                {formatCurrency(record.total_neto)}
              </td>
              <td className="no-responsive" style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                {record.total_with_tax ? formatCurrency(record.total_with_tax) : <span style={{ opacity: 0.4 }}>—</span>}
              </td>
              <td className="no-responsive">
                {renderTaxBadge(record.tax_type)}
              </td>
              <td className="no-responsive">
                {record.expense_category_name
                  ? <span className="category-badge">{record.expense_category_name}</span>
                  : <span style={{ opacity: 0.4 }}>—</span>}
              </td>
              <td className="no-responsive" style={{ fontVariantNumeric: 'tabular-nums', opacity: 0.8 }}>
                {formatCurrency(record.unit_price)}{record.unidad_medida_acronym ? ` / ${record.unidad_medida_acronym}` : ''}
              </td>
              <td>
                <div className="flex items-center justify-center gap-1.5">
                  <button className="mp-btn-icon" onClick={() => handleEdit(record)} title="Editar">
                    <HiOutlinePencilSquare size={16} />
                  </button>
                  <button className="mp-btn-icon mp-btn-icon-danger" onClick={() => handleDelete(record)} title="Eliminar">
                    <HiOutlineTrash size={16} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderMobileView = () => (
    <div className="compras-mobile-view">
      {data.map((record) => (
        <div key={record.ingredient_purchase_id} className="compra-mobile-card">
          <div className="card-header">
            <div className="card-product-info">
              <div className="product-name">
                {record.ingredient_name || "Sin producto"}
              </div>
              {record.provider_name && (
                <span className="provider-badge">{record.provider_name}</span>
              )}
            </div>
            <div className="card-date">
              {record.purchase_date
                ? new Date(record.purchase_date).toLocaleDateString()
                : record.timestamp
                  ? new Date(record.timestamp).toLocaleDateString()
                  : "Sin fecha"}
            </div>
          </div>
          <div className="card-body">
            <div className="card-stat">
              <span className="stat-label">Cantidad</span>
              <span className="stat-value">{record.quantity.toFixed(2)} {record.unidad_medida_name}</span>
            </div>
            <div className="card-stat">
              <span className="stat-label">Total</span>
              <span className="stat-value highlight">{formatCurrency(record.total_neto)}</span>
            </div>
            <div className="card-stat">
              <span className="stat-label">Precio Unit.</span>
              <span className="stat-value">{formatCurrency(record.unit_price)}{record.unidad_medida_acronym ? ` / ${record.unidad_medida_acronym}` : ''}</span>
            </div>
          </div>
          <div className="card-badges-row">
            {renderTaxBadge(record.tax_type)}
            {record.expense_category_name && (
              <span className="category-badge">{record.expense_category_name}</span>
            )}
          </div>
          <div className="card-actions">
            <button
              className="action-button"
              onClick={() => handleEdit(record)}
            >
              <HiOutlinePencilSquare size={14} />
              Editar
            </button>
            <button
              className="action-button delete-button"
              onClick={() => handleDelete(record)}
            >
              <HiOutlineTrash size={14} />
              Eliminar
            </button>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="compras-table-container">
      {data.length === 0 ? (
        <div className="mp-table-card" style={{ padding: '3rem', textAlign: 'center' }}>
          <FaInbox size={32} style={{ opacity: 0.3, marginBottom: '0.75rem' }} />
          <div style={{ opacity: 0.6 }}>No se han generado compras aún!</div>
        </div>
      ) : (
        <>
          {renderDesktopView()}
          {renderMobileView()}
        </>
      )}

      <EditPurchase
        show={showEditModal}
        setShow={setShowEditModal}
        onUpdated={() => {
          fetchPurchases();
        }}
        purchase={editPurchase}
        categories={categories}
      />
    </div>
  );
};

export default PurchasesTable;
